// File: /src/components/ClientForm.js

import React, { useState, useEffect } from 'react';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import PhoneInput from 'react-phone-input-2';
import { countryTranslations } from '../utils/countryTranslations';
import { getOrderedCountries } from '../utils/countryUtils';
import '../styles/ClientForm.css';
import 'react-phone-input-2/lib/style.css';

const convertTranslationsForPhoneInput = (translations) => {
	const converted = {};
	Object.entries(translations).forEach(([code, countryData]) => {
		converted[code] = countryData.display;
	});
	return converted;
};

const sortCountryCodes = (countries) => {
	return countries.map((country) => country.code);
};

const ClientForm = ({
	form,
	handleInputChange,
	addOrUpdateClient,
	editingClient,
}) => {
	const [phone, setPhone] = useState(form.phone || '');
	const [cpfCnpjError, setCpfCnpjError] = useState('');

	useEffect(() => {
		if (!phone) {
			setPhone('+55');
		}
	}, [phone]);

	useEffect(() => {
		setPhone(form.phone || '');
	}, [form.phone]);

	// Função para formatar CPF ou CNPJ conforme o usuário digita
	const formatCpfCnpjInput = (value) => {
		const digits = value.replace(/\D/g, '');
		if (digits.length <= 11) {
			// Formatação para CPF: 000.000.000-00
			let cpfFormatted = '';
			if (digits.length > 0) {
				cpfFormatted = digits.substring(0, Math.min(3, digits.length));
			}
			if (digits.length >= 4) {
				cpfFormatted +=
					'.' + digits.substring(3, Math.min(6, digits.length));
			}
			if (digits.length >= 7) {
				cpfFormatted +=
					'.' + digits.substring(6, Math.min(9, digits.length));
			}
			if (digits.length >= 10) {
				cpfFormatted +=
					'-' + digits.substring(9, Math.min(11, digits.length));
			}
			return cpfFormatted;
		} else {
			// Formatação para CNPJ: 00.000.000/0000-00
			let cnpjFormatted = '';
			if (digits.length > 0) {
				cnpjFormatted = digits.substring(0, Math.min(2, digits.length));
			}
			if (digits.length >= 3) {
				cnpjFormatted +=
					'.' + digits.substring(2, Math.min(5, digits.length));
			}
			if (digits.length >= 6) {
				cnpjFormatted +=
					'.' + digits.substring(5, Math.min(8, digits.length));
			}
			if (digits.length >= 9) {
				cnpjFormatted +=
					'/' + digits.substring(8, Math.min(12, digits.length));
			}
			if (digits.length >= 13) {
				cnpjFormatted +=
					'-' + digits.substring(12, Math.min(14, digits.length));
			}
			return cnpjFormatted;
		}
	};

	// Função para tratar a mudança do input de CPF/CNPJ
	const handleCpfCnpjChange = (e) => {
		const { name, value } = e.target;
		const formatted = formatCpfCnpjInput(value);
		// Atualiza o estado chamando a função de input já existente
		handleInputChange({ target: { name, value: formatted } });
		// Limpa o erro enquanto digita
		setCpfCnpjError('');
	};

	// Handler para o telefone (sem validação de comprimento)
	const handlePhoneChange = (value, country) => {
		const numberOnly = value.replace(/\D/g, '');
		let formatted = value;

		if (country.countryCode === 'br') {
			const ddd = numberOnly.slice(2, 4);
			const numero = numberOnly.slice(4);

			if (ddd) {
				if (numero.length <= 8) {
					const primeiraParte = numero.slice(0, 4);
					const segundaParte = numero.slice(4);
					formatted = `+55 (${ddd}) ${primeiraParte}${
						numero.length > 4 ? '-' : ''
					}${segundaParte}`;
				} else {
					const primeiraParte = numero.slice(0, 5);
					const segundaParte = numero.slice(5);
					formatted = `+55 (${ddd}) ${primeiraParte}${
						numero.length > 5 ? '-' : ''
					}${segundaParte}`;
				}
			} else {
				formatted = `+55 (${numberOnly.slice(2)}`;
			}
		}

		setPhone(formatted);
		handleInputChange({ target: { name: 'phone', value: formatted } });
	};

	// Simplificar o handleSubmit para validar apenas CPF/CNPJ
	const handleSubmit = (e) => {
		e.preventDefault();
		const cpfCnpjDigits = form.cpf_cnpj.replace(/\D/g, '');
		const isCpfCnpjValid =
			cpfCnpjDigits.length <= 11
				? cpf.isValid(cpfCnpjDigits)
				: cnpj.isValid(cpfCnpjDigits);

		if (!isCpfCnpjValid) {
			setCpfCnpjError('CPF/CNPJ inválido');
			return;
		}

		addOrUpdateClient(e);
	};

	return (
		<form
			className="clients-form"
			onSubmit={handleSubmit}
			autoComplete="off"
		>
			<h2 className="form-title">
				{editingClient ? 'EDITAR CLIENTE' : 'ADICIONAR CLIENTE'}
			</h2>
			<div className="form-field">
				<label>Nome</label>
				<input
					className="full-width"
					name="name"
					placeholder="Digite o nome do cliente"
					value={form.name}
					onChange={handleInputChange}
					required
				/>
			</div>
			<div className="form-field">
				<label>Email</label>
				<input
					name="email"
					placeholder="Digite o email do cliente"
					value={form.email}
					onChange={handleInputChange}
					required
					type="email"
				/>
			</div>
			<div className="form-field">
				<label>CPF/CNPJ</label>
				<input
					name="cpf_cnpj"
					placeholder="Digite o CPF/CNPJ"
					value={form.cpf_cnpj}
					onChange={handleCpfCnpjChange}
					required
				/>
				{cpfCnpjError && (
					<p
						style={{
							color: 'red',
							fontSize: '0.9rem',
							margin: '4px 0',
						}}
					>
						{cpfCnpjError}
					</p>
				)}
			</div>
			<div className="form-field">
				<label>Telefone</label>
				<div className="phone-input-container">
					<PhoneInput
						country="br"
						defaultCountry="br"
						value={phone}
						onChange={handlePhoneChange}
						inputProps={{
							required: true,
							autoComplete: 'off',
						}}
						countryCodeEditable={false}
						enableSearch={true}
						disableCountryCode={false}
						specialLabel=""
						inputStyle={{ width: '100%', height: '42px' }}
						containerStyle={{ width: '100%' }}
						dropdownStyle={{ width: '260px' }}
						searchPlaceholder="Buscar país..."
						searchNotFound="País não encontrado"
						preferredCountries={['br', 'us', 'pt']}
						priority={{ br: 0 }}
						localization={convertTranslationsForPhoneInput(
							countryTranslations
						)}
						onlyCountries={sortCountryCodes(getOrderedCountries())}
					/>
				</div>
			</div>
			<div className="form-field">
				<label>Endereço</label>
				<input
					className="full-width"
					name="address"
					placeholder="Digite o endereço do cliente"
					value={form.address}
					onChange={handleInputChange}
				/>
			</div>
			<button type="submit">
				{editingClient ? 'Salvar Alterações' : 'Adicionar Cliente'}
			</button>
		</form>
	);
};

export default ClientForm;
